/**
 * hk_os · <OfflineSyncIndicator>
 * ────────────────────────────────────────────────────────────────────
 * Reusable status badge showing connectivity + sync state. Consumers
 * pass a `state` in {online, syncing, offline, queued, failed} plus an
 * optional `queuedCount`. Wires into `navigator.onLine` when state is
 * omitted (self-observing mode) so simple call sites can drop it in.
 *
 * Props:
 *   state         — one of the five (default derived from navigator.onLine)
 *   queuedCount   — integer, shown next to Queued / Offline states
 *   onRetry       — click handler shown when state = failed
 *   dense         — compact chip (default false)
 *   testid
 */
import React, { useEffect, useState } from "react";
import {
  Wifi, WifiOff, RefreshCw, ClipboardList, AlertTriangle, CheckCircle2,
} from "lucide-react";

const STATE_META = {
  online:  { label: "Online",   cls: "bg-emerald-50 text-emerald-800 border-emerald-300", icon: Wifi },
  syncing: { label: "Syncing",  cls: "bg-sky-50 text-sky-800 border-sky-300",             icon: RefreshCw, spin: true },
  offline: { label: "Offline",  cls: "bg-slate-100 text-slate-700 border-slate-300",      icon: WifiOff },
  queued:  { label: "Queued",   cls: "bg-amber-50 text-amber-900 border-amber-300",       icon: ClipboardList },
  failed:  { label: "Failed",   cls: "bg-red-50 text-red-800 border-red-300",             icon: AlertTriangle },
  ok:      { label: "Synced",   cls: "bg-emerald-50 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
};

function useNavigatorState() {
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);
  return online;
}

export function OfflineSyncIndicator({
  state,
  queuedCount = 0,
  onRetry,
  dense = false,
  testid,
}) {
  const online = useNavigatorState();
  const resolved = state || (online ? "online" : "offline");
  const meta = STATE_META[resolved] || STATE_META.online;
  const Icon = meta.icon;

  return (
    <span
      data-testid={testid || `offline-sync-${resolved}`}
      className={[
        "inline-flex items-center rounded-full border font-semibold whitespace-nowrap",
        meta.cls,
        dense ? "px-2 py-0.5 text-[11px] gap-1"
               : "px-3 py-1 text-[13px] gap-1.5",
      ].join(" ")}
    >
      <Icon
        className={`${dense ? "w-3 h-3" : "w-3.5 h-3.5"} ${meta.spin ? "animate-spin" : ""}`}
        strokeWidth={2.4}
      />
      <span>{meta.label}</span>
      {(resolved === "queued" || (resolved === "offline" && queuedCount > 0)) && (
        <span
          data-testid={testid ? `${testid}-count` : "offline-sync-count"}
          className="ml-1 rounded-full bg-white/70 px-1.5 text-[11px] font-bold tabular-nums"
        >
          {queuedCount}
        </span>
      )}
      {resolved === "failed" && onRetry && (
        <button type="button"
          data-testid={testid ? `${testid}-retry` : "offline-sync-retry"}
          onClick={onRetry}
          className="ml-1 rounded-full bg-red-600 text-white px-2 py-0.5 text-[11px] font-bold hover:bg-red-700"
        >
          Retry
        </button>
      )}
    </span>
  );
}

export default OfflineSyncIndicator;
